import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { MapRef, Marker } from 'react-map-gl';

import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import 'mapbox-gl/dist/mapbox-gl.css';

import { IconLocation } from '@components/icons/location';
import { SelectAsync, SelectAsyncProps } from '@components/select';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { debounce } from '@utils/browser';

export type LocationSearchProps = {
  placeholder: string;
  label: string;
  noLocationMatchingText: string;
  initialText: string;
  mapToken: string;
  onSelect: (location: LocationSearchValue) => void;
  height?: number;
  width?: number;
  defaultValue?: LocationSearchValue | null;
};

export type LocationSearchValue = {
  address: string;
  longitude: number;
  latitude: number;
};

type MapBoxFeatureResponse = {
  place_name: string;
  geometry: {
    coordinates: [number, number];
  };
};

export function LocationSearch({
  height = 500,
  width = 800,
  mapToken,
  onSelect,
  placeholder,
  label,
  noLocationMatchingText,
  initialText,
  defaultValue,
}: LocationSearchProps) {
  const { t } = useScopedTranslate(locale);
  const mapRef = useRef<MapRef>(null);
  const [failedQuery, setFailedQuery] = useState<string | null>(null);
  // Responses can arrive out of order; only the latest search decides the failure message.
  const latestQueryRef = useRef<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [longitude, setLongitude] = useState<number | null>(defaultValue?.longitude ?? null);
  const [latitude, setLatitude] = useState<number | null>(defaultValue?.latitude ?? null);
  const flyToMap = useCallback((longitude: number, latitude: number) => {
    mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 17, duration: 2500 });
  }, []);
  const onMapLoad = useCallback(() => {
    setMapLoaded(true);
  }, []);
  useEffect(() => {
    if (
      mapLoaded &&
      defaultValue &&
      defaultValue.longitude !== null &&
      defaultValue.latitude !== null
    ) {
      flyToMap(defaultValue.longitude, defaultValue.latitude);
    }
  }, [defaultValue, mapLoaded, flyToMap]);
  const searchLocation: SelectAsyncProps<LocationSearchValue>['loadOptions'] = useMemo(
    () =>
      debounce((inputValue, callback) => {
        latestQueryRef.current = inputValue;
        if (inputValue.trim() === '') {
          callback([]);
          return;
        }
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          inputValue,
        )}.json?access_token=${mapToken}&proximity=ip&fuzzyMatch=false`;
        fetch(url)
          .then((response) => {
            // Mapbox answers 422 for a query it cannot geocode (over 256 characters or
            // 20 words); retrying the same text never helps, so it reads as no match.
            if (response.status === 422) return { features: [] };
            if (!response.ok) {
              throw new Error(`Mapbox geocoding responded ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            data = data.features.map((d: MapBoxFeatureResponse) => ({
              label: (
                <Stack space={2} orientation="horizontal" align="center">
                  <IconLocation color="purple100" />
                  {d.place_name}
                </Stack>
              ),
              filterText: d.place_name,
              value: {
                address: d.place_name,
                longitude: d.geometry.coordinates[0],
                latitude: d.geometry.coordinates[1],
              },
            }));
            if (latestQueryRef.current === inputValue) setFailedQuery(null);
            callback(data);
          })
          .catch((error) => {
            Sentry.captureException(error);
            if (latestQueryRef.current === inputValue) setFailedQuery(inputValue);
            callback([]);
          });
      }, 300),
    [mapToken],
  );
  return (
    <Stack space={4}>
      <SelectAsync
        placeholder={defaultValue?.address ?? placeholder}
        label={label}
        defaultOptions={false}
        loadOptions={searchLocation}
        noOptions={({ inputValue }) => {
          if (inputValue === failedQuery) return t('search_failed');
          if (inputValue !== '') return `${noLocationMatchingText} ` + inputValue;
          return initialText;
        }}
        onChange={(value: LocationSearchValue) => {
          flyToMap(value.longitude, value.latitude);
          setLongitude(value.longitude);
          setLatitude(value.latitude);
          onSelect(value);
        }}
      />
      <MapContainer space={0}>
        <Map
          ref={mapRef}
          onLoad={onMapLoad}
          key={mapToken}
          style={{ height, width }}
          interactive={false}
          mapboxAccessToken={mapToken}
          mapStyle="mapbox://styles/mapbox/streets-v9"
        >
          {latitude !== null && longitude !== null && (
            <Marker key={latitude} latitude={latitude} longitude={longitude} anchor="bottom">
              <IconLocation color="elementBlue100" />
            </Marker>
          )}
        </Map>
      </MapContainer>
    </Stack>
  );
}

const locale = {
  en: {
    search_failed: 'Could not search for addresses. Try again.',
  },
  is: {
    search_failed: 'Ekki tókst að leita að heimilisföngum. Reyndu aftur.',
  },
  dk: {
    search_failed: 'Kunne ikke søge efter adresser. Prøv igen.',
  },
};

const MapContainer = styled(Stack)({
  backgroundColor: theme.color.purple10,
  borderRadius: '0.25rem',
  padding: '1rem',
  width: '100%',
  height: '100%',
  '.mapboxgl-ctrl-bottom-right, .mapboxgl-ctrl-bottom-left': {
    display: 'none',
  },
});
